/**
 * Application Entry Point
 *
 * This is where React boots up and mounts to the DOM.
 *
 * React uses a "virtual DOM" - a JavaScript representation of the UI.
 * When state changes, React compares the virtual DOM to the real DOM
 * and only updates what actually changed (efficient).
 *
 * StrictMode is a development tool that:
 * - Warns about deprecated patterns
 * - Double-invokes some functions to detect side effects
 * - Helps find subtle bugs before they cause problems
 *
 * Learn more: https://react.dev/reference/react/StrictMode
 */

import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';

// Import global styles - these apply to the entire application
import './styles/global.css';

// Install browser API fallback if Electron is not available
import { installBrowserAPI } from './utils/browserAPI';
installBrowserAPI();

/**
 * Find the root element in index.html and mount React
 *
 * createRoot is the modern React 18+ API for rendering.
 * The non-null assertion (!) tells TypeScript we're sure the element exists.
 */
const rootElement = document.getElementById('root')!;
const root = ReactDOM.createRoot(rootElement);

/**
 * Render the app
 *
 * <React.StrictMode> wraps the app in development checks.
 * It's automatically removed in production builds for performance.
 */
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);
